import { Form } from '@hologram-dimension/form';
import { Loader } from '@hologram-dimension/loader';
import { RadioField } from '@hologram-dimension/radio-field';
import useToggleTunneling from 'api/legacy/devices/useToggleTunneling';
import useSimDetails from 'api/sims/useSimDetails';
import { useUserInfo } from 'hooks';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import useOrganization from 'state/contexts/Organization/useOrganization';
import type { HologramSimIdsMap, OnSubmit } from 'types/common';
import { BannerNotification } from '@hologram-dimension/banner-notification';
import styles from './ManageTunneling.module.css';

interface ManageTunnelingProps {
  sims: HologramSimIdsMap;
  onProcessing: (value: boolean) => void;
  onDone: (sims: Array<HologramSimId>) => void;
}

type TunnelingValue = 'enable' | 'disable';

interface MessageMethodForm {
  newTunnelingValue?: TunnelingValue;
}

const ManageTunneling = forwardRef<OnSubmit, ManageTunnelingProps>(
  ({ sims, onProcessing, onDone }, ref) => {
    const simId = sims?.size === 1 ? sims?.keys().next().value : undefined;

    const { simDetails, isLoading } = useSimDetails(simId);

    const defaultValue = useMemo(() => {
      let newDefaultValue: TunnelingValue | undefined;
      if (simId && !isLoading) {
        newDefaultValue = simDetails?.tunnelable === 1 ? 'disable' : 'enable';
      }
      return newDefaultValue;
    }, [isLoading, simDetails?.tunnelable, simId]);

    const {
      register,
      watch,
      setValue,
      handleSubmit,
      formState: { errors },
    } = useForm<MessageMethodForm>({
      mode: 'onSubmit',
      defaultValues: { newTunnelingValue: defaultValue },
    });

    const [successMessage, setSuccessMessage] = useState<string>();
    const [lastErrorDevices, setLastErrorDevices] = useState<Map<DeviceId, string>>();
    const [apiError, setApiError] = useState<string>();

    const newTunnelingValue = watch('newTunnelingValue');

    useEffect(() => {
      if (simId && !isLoading && defaultValue && !newTunnelingValue) {
        setValue('newTunnelingValue', defaultValue);
      }
    }, [defaultValue, isLoading, newTunnelingValue, setValue, simId]);

    const { isSuperAdmin } = useUserInfo();
    const organizationId = useOrganization();
    const toggleTunnelingApi = useToggleTunneling({ sims, organizationId, isSuperAdmin });

    const simIdReverseLookup = useMemo(
      () => new Map([...sims.entries()].map(([key, value]) => [value, key])),
      [sims]
    );

    const commitStateChange = useCallback<SubmitHandler<MessageMethodForm>>(
      async (data) => {
        onProcessing(true);
        try {
          const result = await toggleTunnelingApi(data.newTunnelingValue === 'enable');

          const successfulDevices: Array<DeviceId> = [];
          const errorDevices = new Map<DeviceId, string>();

          if (result) {
            result.forEach((element) => {
              if (element.success) {
                successfulDevices.push(element.deviceId);
              } else {
                errorDevices.set(element.deviceId, element.error ?? 'Unknown error');
              }
            });
          }

          if (successfulDevices.length > 0) {
            const message = `Secure tunneling ${data.newTunnelingValue === 'enable' ? 'enabled' : 'disabled'}${sims.size > 1 ? ` for ${successfulDevices.length} of ${sims.size} SIMs` : ''}.`;
            setSuccessMessage(message);
            onDone(
              successfulDevices
                .map((deviceId) => simIdReverseLookup.get(deviceId))
                .filter((deviceId) => deviceId !== undefined)
            );
          }
          if (errorDevices.size > 0) {
            setLastErrorDevices(errorDevices);
          }
        } catch (ex) {
          if (typeof ex === 'string') {
            setApiError(ex);
          } else {
            setApiError(`An error occurred: ${ex}`);
          }
        } finally {
          onProcessing(false);
        }
      },
      [onDone, onProcessing, simIdReverseLookup, sims.size, toggleTunnelingApi]
    );

    const onSubmit = handleSubmit(commitStateChange);

    const formRef = useRef<HTMLFormElement>(null);

    const manualSubmit = useCallback(async () => {
      formRef.current?.requestSubmit();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit: manualSubmit,
      }),
      [manualSubmit]
    );

    const content = useMemo(() => {
      if (isLoading) {
        return <Loader>{`Checking selected ${simId ? 'SIM' : 'SIMs'}...`}</Loader>;
      }

      const hasError = lastErrorDevices && lastErrorDevices.size > 0;
      if (successMessage || hasError) {
        return (
          <>
            {successMessage && (
              <BannerNotification variant="success">{successMessage}</BannerNotification>
            )}
            {hasError && (
              <BannerNotification>
                Enable secure tunneling failed for {lastErrorDevices.size} SIMs:
                <ul className={styles.errorList}>
                  {[...lastErrorDevices.entries()].map(([deviceId]) => (
                    <li key={deviceId}>
                      SIM ID {simIdReverseLookup.get(deviceId)}, Device ID {deviceId}
                    </li>
                  ))}
                </ul>
              </BannerNotification>
            )}
          </>
        );
      }

      if (apiError) {
        return <BannerNotification variant="system-error">{apiError}</BannerNotification>;
      }

      const forceEnable = !(simId && simDetails?.tunnelable === 1);
      const forceDisable = !(simId && simDetails?.tunnelable !== 1);

      return (
        <Form onSubmit={onSubmit} ref={formRef}>
          <RadioField
            label="Secure device tunneling"
            helpText={simId ? 'All selected SIMs will be updated to use this setting.' : undefined}
            options={[
              {
                label: `Enable tunneling${forceDisable ? ' (tunneling is enabled)' : ''}`,
                value: 'enable',
                disabled: forceDisable,
              },
              {
                label: `Disable tunneling${forceEnable ? ' (tunneling is disabled)' : ''}`,
                value: 'disable',
                disabled: forceEnable,
              },
            ]}
            validationMessage={errors.newTunnelingValue?.message}
            invalid={!!errors.newTunnelingValue}
            {...register('newTunnelingValue', {
              required: 'Please select a new tunneling state',
            })}
          />
          {newTunnelingValue === 'disable' && (
            <BannerNotification variant="caution">
              Disabling secure tunneling will remove the ability to communicate with devices through
              these SIMs using our Spacebridge client.
            </BannerNotification>
          )}
        </Form>
      );
    }, [
      apiError,
      errors.newTunnelingValue,
      isLoading,
      lastErrorDevices,
      newTunnelingValue,
      onSubmit,
      register,
      simDetails?.tunnelable,
      simId,
      simIdReverseLookup,
      successMessage,
    ]);

    return (
      <div className={styles.wrapper}>
        <div className={styles.forms}>{content}</div>
      </div>
    );
  }
);

export default ManageTunneling;
